import { useEffect, useRef } from "react";
import { getPortalUrl } from "../lib/portal-url";
import { readShareTokenFromPath } from "../share/token";

const HEARTBEAT_MS = 30_000;

function usageUrl(path: string): string {
  return `${getPortalUrl()}/api/tools/usage${path}`;
}

async function postJson(path: string, body: unknown): Promise<Response> {
  return fetch(usageUrl(path), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    keepalive: true,
  });
}

function beacon(path: string, body: unknown): void {
  if (typeof navigator === "undefined" || !navigator.sendBeacon) {
    void postJson(path, body).catch(() => {});
    return;
  }
  const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
  navigator.sendBeacon(usageUrl(path), blob);
}

export interface UseToolUsageArgs {
  slug: string;
  ready: boolean;
  shareToken?: string | null;
}

interface ActivityEntry {
  activityId: string | null;
  ending: boolean;
}

interface UsageApi {
  startActivity(key: string, activity: string, audience?: string): void;
  endActivity(key: string): void;
}

let currentApi: UsageApi | null = null;

/** Start een activiteit (no-op als `key` al loopt). Door EMDR-app aangeroepen. */
export function trackActivityStart(
  key: string,
  activity: string,
  audience?: string,
): void {
  currentApi?.startActivity(key, activity, audience);
}

/** Beëindig de activiteit bij `key` (no-op als er geen loopt). */
export function trackActivityEnd(key: string): void {
  currentApi?.endActivity(key);
}

export function useToolUsage({ slug, ready, shareToken }: UseToolUsageArgs): void {
  const usageIdRef = useRef<string | null>(null);
  const activitiesRef = useRef<Map<string, ActivityEntry>>(new Map());
  const shareTokenRef = useRef<string | null>(shareToken ?? null);

  useEffect(() => {
    shareTokenRef.current = shareToken ?? readShareTokenFromPath() ?? null;
  }, [shareToken]);

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;

    async function open() {
      try {
        const res = await postJson("/open", {
          toolSlug: slug,
          shareToken: shareTokenRef.current,
        });
        const data = (await res.json().catch(() => ({}))) as { usageId?: string };
        if (!cancelled && data.usageId) {
          usageIdRef.current = data.usageId;
        }
      } catch {
        // analytics mag nooit de tool breken
      }
    }
    void open();

    const heartbeat = () => {
      const usageId = usageIdRef.current;
      if (usageId) void postJson("/heartbeat", { usageId }).catch(() => {});
    };
    const interval = window.setInterval(heartbeat, HEARTBEAT_MS);

    const api: UsageApi = {
      startActivity(key, activity, audience) {
        if (activitiesRef.current.has(key)) return;
        activitiesRef.current.set(key, { activityId: null, ending: false });
        const usageId = usageIdRef.current;
        postJson("/activity", {
          toolSlug: slug,
          activity,
          audience: audience ?? null,
          usageId,
          shareToken: shareTokenRef.current,
        })
          .then((r) => r.json().catch(() => ({})))
          .then((d: { activityId?: string }) => {
            const entry = activitiesRef.current.get(key);
            if (entry && d.activityId) entry.activityId = d.activityId;
          })
          .catch(() => {});
      },
      endActivity(key) {
        const entry = activitiesRef.current.get(key);
        if (!entry || entry.ending) return;
        entry.ending = true;
        const activityId = entry.activityId;
        activitiesRef.current.delete(key);
        if (activityId) beacon("/activity/end", { activityId });
      },
    };
    currentApi = api;

    const onClose = () => {
      const usageId = usageIdRef.current;
      if (usageId) beacon("/close", { usageId });
      for (const [, entry] of activitiesRef.current) {
        if (entry.activityId) beacon("/activity/end", { activityId: entry.activityId });
      }
      activitiesRef.current.clear();
    };
    window.addEventListener("beforeunload", onClose);
    window.addEventListener("pagehide", onClose);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("beforeunload", onClose);
      window.removeEventListener("pagehide", onClose);
      if (currentApi === api) currentApi = null;
      onClose();
    };
  }, [ready, slug]);
}
